import { Add as AddIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { PrimaryButton } from 'src/components/Buttons/PrimaryButton';
import { Show } from 'src/components/Show';
import { RewardType } from 'src/global/enums/project.enum';
import type { ProjectAdminWalletType } from 'src/global/schemas/project.schema';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { formatNumberWithComma } from 'src/utils/helpers';

import { PhaseItem } from './PhaseItem';

export interface DistributionPhaseProps {
  adminWallet?: ProjectAdminWalletType;
  isDisabled?: boolean;
}

export function DistributionPhase({ adminWallet, isDisabled = false }: DistributionPhaseProps) {
  const { hasDeployed } = useDeployment();
  const {
    control,
    trigger,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useFormContext<ProjectInputs>();
  const { fields, remove, append, update } = useFieldArray({
    control,
    name: 'phases',
  });
  const tier = watch('tier');
  const tierLayer = useWatch({
    name: 'tier.layer',
    control,
  });
  const phases = watch('phases');
  const tierAutoAdjustment = watch('tier.autoAdjustment');
  const nodeSupplyCap = watch('tier.nodeSupplyCap');
  const referral = watch('referral');
  const totalLayerSupply = (watch('tier.layer') || []).reduce((sum, tier) => sum + tier.total, 0);
  const limitNodeSupply = tierAutoAdjustment ? nodeSupplyCap : totalLayerSupply;

  const totalNodeSupply = useMemo(() => {
    if (tierAutoAdjustment) {
      return nodeSupplyCap || 0;
    }
    return tierLayer?.reduce((total, tierLayer) => {
      const tierLayerTotal = tierLayer.total || 0;
      return total + tierLayerTotal;
    }, 0);
  }, [nodeSupplyCap, tierAutoAdjustment, tierLayer]);

  useEffect(() => {
    if (limitNodeSupply) {
      const newPhases = watch('phases').map(phase => ({
        ...phase,
        limitNodeSupply,
      }));
      setValue('phases', newPhases);
      // trigger('phases');
    }
  }, [JSON.stringify(tier)]);

  return (
    <Box width={'100%'} height={'100%'} display={'flex'} flexDirection={'column'}>
      <Typography fontWeight={500} fontSize={'16px'} color='white' paddingBottom={2}>
        Funding Distribution by Phase
      </Typography>
      <Typography fontWeight={400} fontSize={'14px'} color='white' paddingBottom={3}>
        Note: The total funding percentage allocated across all parties must be equal to{' '}
        <strong>{referral.rewardType === RewardType.ERC_20 ? 100 - (referral?.percentage ?? 0) : '100'}%</strong>, as
        the remaining percentage will be used for the referral percentage if ERC-20 token rewards are chosen.{' '}
      </Typography>
      <Typography fontWeight={400} fontSize={'14px'} color='white' paddingBottom={3}>
        Total Core Supply: <strong>{formatNumberWithComma(totalNodeSupply ?? 0)} Nodes</strong>
      </Typography>
      <Box>
        {!fields.length ? (
          <Box width={'100%'}>
            <Box
              width={'100%'}
              display={'flex'}
              justifyContent={'center'}
              paddingY={2}
              sx={{
                color: 'white',
                border: `1px solid ${errors.phases ? colors.error : colors.neutral015}`,
                borderRadius: '4px',
              }}>
              No phases added
            </Box>
            {errors.phases ? (
              <Typography paddingTop={1} color={colors.error} fontSize={'14px'}>
                {errors.phases.root?.message || errors.phases.message}
              </Typography>
            ) : null}
          </Box>
        ) : (
          <Stack width={'100%'} gap={3}>
            {fields.map((field, index) => (
              <PhaseItem
                onEdit={() => {
                  if (isDisabled) return false;
                  update(index, { ...field, isEdit: true });
                }}
                index={index}
                isModifying={field.isEdit}
                key={field.id}
                onDelete={e => {
                  e.preventDefault();
                  if (isDisabled) return false;
                  remove(index);
                }}
                onSave={async () => {
                  if (isDisabled) return false;
                  const isValid = await trigger(`phases`);
                  const currentPhase = phases[index];
                  if (isValid && currentPhase) update(index, { ...currentPhase, isEdit: false });
                }}
              />
            ))}
            {errors.phases ? (
              <Typography color={colors.error} fontSize={'14px'}>
                {errors.phases?.root?.message || errors.phases.message}
              </Typography>
            ) : null}
          </Stack>
        )}
        <Show when={!hasDeployed}>
          <Box marginTop={2}>
            <PrimaryButton
              isFullWidth
              startIcon={<AddIcon />}
              onClick={() => {
                if (isDisabled) return false;
                if (errors.phases) {
                  clearErrors('phases');
                }
                append({
                  totalNode: 0,
                  recipients: [{ address: adminWallet?.addr ?? '', percent: adminWallet?.minPercent ?? 0 }],
                  isEdit: true,
                  limitNodeSupply,
                  referalPercent: referral.percentage,
                  rewardType: referral.rewardType,
                });
              }}>
              Add Phase
            </PrimaryButton>
          </Box>
        </Show>
      </Box>
    </Box>
  );
}
